/* global RTCSessionDescription, RTCPeerConnection, MediaStream */
import { notify } from 'app/services/errorReporter';
import Agent from 'app/utils/queues/Agent';
import { IValueTransformMiddleware } from 'app/utils/queues/constants';
import debounce from 'lodash/debounce';
import {
  RTC_ON,
  RTC_CONNECTING,
  RENEGOTIATION_MIN_INTERVAL,
  PEER_WATCHDOG_CONFIG,
} from 'app/services/rtc/constants';
import { EventEmitter } from 'events';
import WebRTCApi from 'app/services/rtc/WebRTCApi';
import NegotiationState, { RenegotiationReason, Mids, IRenegotiationInit } from 'app/services/rtc/NegotiationState';
import { printDebugTree } from 'app/utils/debugTree';
import Watchdog from './Watchdog';
import { throwIfCancelled } from 'app/utils/cancelToken';
import {
  ISdpAnswerMessage,
  ISdpOfferMessage,
  IIceCandidateMessage,
  INegotiationNeededMessage,
  NegotiationMessageType,
  NegotiationMessageCodec,
} from '@engoo/realtime-protocol/signaling/negotiation';
import { decodeWithDefault } from 'app/utils/ioTs';
import { Optional } from 'app/utils/types';
import { is } from 'immutable';

const DEBUG_PREFIX  = 'RTC';

export enum PeerConnectionEvents {
  remoteAudioStream =     'remoteAudioStream',
  remoteVideoStream =     'remoteVideoStream',
  remoteScreenStream =    'remoteScreenStream',
  connectionStateChange = 'connectionStateChange',
  connectionError =       'connectionError',
  connectionTimeout =     'connectionTimeout',
}

interface ISendMethods {
  offer: (message: Omit<ISdpOfferMessage, 'type'>) => Promise<ISdpAnswerMessage>;
  negotiationNeeded: (message: Omit<INegotiationNeededMessage, 'type'>) => Promise<void>;
  iceCandidate: (...args: unknown[]) => Promise<void>;
}

interface IPeerConnectionOptions {
  id: string;
  isOfferer: boolean;
  tracer?: NegotiationState['tracer'];
  channels:  NegotiationState['channels'];
  config: NegotiationState['config'];
  send: ISendMethods;
}

const supportsTrackEvents = () => {
  return 'ontrack' in RTCPeerConnection.prototype;
};

export interface ISdpDump {
  local?: RTCSessionDescription;
  remote?: RTCSessionDescription;
}

export class PeerConnection extends EventEmitter {
  public debug: { fail: () => void };
  private watchdog: Watchdog;
  private state: Agent<NegotiationState>;
  private isOfferer: boolean;
  private debounced: { onNegotiationNeeded: ReturnType<typeof debounce> };
  private send: ISendMethods;

  public constructor({ id, config, channels, send, isOfferer, tracer }: IPeerConnectionOptions) {
    super();

    this.isOfferer = isOfferer;
    this.send      = send;
    this.watchdog  = new Watchdog({
      ...PEER_WATCHDOG_CONFIG,
      name: id ? `peer (${id})` : 'unknown peer',
    });

    // We're debouncing these because they can happen multiple times
    // in a row when modifying streams locally or remotely
    this.debounced = {
      onNegotiationNeeded: debounce(
        this.onNegotiationNeeded,
        RENEGOTIATION_MIN_INTERVAL,
        {
          leading: true,
          trailing: true,
        },
      ),
    };

    // Events coming from the connection itself
    const connectionEventMap = {
      negotiationneeded:        this.debounced.onNegotiationNeeded,
      icecandidate:             this.onIceCandidate,
      iceconnectionstatechange: this.onConnectionStateChange,
      icegatheringstatechange:  this.onConnectionStateChange,
      connectionstatechange:    this.onConnectionStateChange,
      signalingstatechange:     this.onConnectionStateChange,
      ...(
        // Use `track` event by default, only fallback to `addstream`
        // if `track` is not supported (Edge, older Chrome)
        supportsTrackEvents()
        ? { track: this.onRemoteTrack }
        : { addstream: this.onRemoteStream }
      ),
    };

    const watchdogEventMap = {
      warning: (timeToReconnect: number) => this.state.update((state) => {
        // It's possible that the connection becomes stable or
        // explicitly closed between the call to this.state.update
        // and to when this function is called on the queue
        if (state.connectionState === RTC_ON || !state.connection) {
          this.watchdog.stop();
          return state;
        }

        this.emit(PeerConnectionEvents.connectionTimeout, {
          timeToReconnect,
          triggeredAt: Date.now(),
        });

        return state;
      }),
      timeout: () => this.state.update((state) => {
        if (state.connectionState === RTC_ON || !state.connection) {
          return state;
        }

        this.emit(PeerConnectionEvents.connectionTimeout, undefined);

        this.debounced.onNegotiationNeeded({
          reset: true,
          reason: RenegotiationReason.Timeout,
        });

        return state;
      }),
    };

    this.watchdog.bindEvents(watchdogEventMap);

    this.state = new Agent({
      init: new NegotiationState({
        config,
        channels,
        tracer,
        eventMap: connectionEventMap,
      }).updateStateIndicators(),
      middleware: this.middleware,
    });

    this.debug = {
      fail: () => {
        this.watchdog.stop();
        this.state.update(state => state.stop());
      },
    };
  }

  public close() {
    this.watchdog.stop();
    this.debounced.onNegotiationNeeded.cancel();
    this.state.abort();
    this.state.update(state => state.close());
  }

  public dispatch(data: unknown): Promise<ISdpAnswerMessage> | Promise<null> | null {
    const message = decodeWithDefault(NegotiationMessageCodec, data, null);
    if (!message) {
      return null;
    }
    // There should be a case for every NegotiationMessageType
    switch (message.type) {
      case NegotiationMessageType.NegotiationNeeded:
        return this.isOfferer ? this.receiveNegotiationNeeded(message) : null;
      case NegotiationMessageType.SdpOffer:
        return !this.isOfferer ? this.negotiateAnswer(message) : null;
      case NegotiationMessageType.IceCandidate:
        return this.receiveIceCandidate(message);
    }
  }

  public reset() {
    this.watchdog.stop();
    this.state.abort();
    this.state.update((state) => state.setError(null));
    this.emit(PeerConnectionEvents.connectionTimeout, undefined);
    this.debounced.onNegotiationNeeded({ reset: true });
  }

  public async setLocalAudioStream(stream: MediaStream) {
    await this.state.update((state) => state.setLocalAudioStream(stream));
  }

  public async setLocalVideoStream(stream: MediaStream) {
    await this.state.update((state) => state.setLocalVideoStream(stream));
  }

  public async setLocalScreenStream(stream: MediaStream) {
    await this.state.update((state) => state.setLocalScreenStream(stream));
  }

  public async getConnectionState() {
    return (await this.state.update(state => state)).connectionState;
  }

  public async dumpCurrentSdps(): Promise<ISdpDump> {
    const connection = (await this.state.update(state => state)).connection;

    return {
      // Return the most up-to-date description which should include
      // also trickled candidates, normalize to undefined
      local: connection?.localDescription ?? undefined,
      remote: connection?.remoteDescription ?? undefined,
    };
  }

  private middleware: IValueTransformMiddleware<NegotiationState> = async (state, update, cancelToken) => {
    let result = state;

    try {
      for (const check of [
        (_: NegotiationState, previousState: NegotiationState) => update(previousState, cancelToken),
        this.renegotiate,
        this.checkLocalStreams,
        this.scheduleConnectionTimeoutCheck,
        this.checkState,
        this.checkRemoteStreams,
        this.checkMids,
      ]) {
        if (cancelToken.isCancelled()) {
          return result;
        }
        const previous = result;
        result = (await check(state, result)).updateStateIndicators();
        printDebugTree(DEBUG_PREFIX, previous, result);
      }
    } catch (error) {
      if (!cancelToken.isCancelled()) {
        notify(error, { name: 'PeerConnection' });
        const previous = result;
        result = result.setError(error);
        printDebugTree(DEBUG_PREFIX, previous, result);
      }
    }

    return result;
  };

  private renegotiate = async (prev: NegotiationState, next: NegotiationState) => {
    const { renegotiation, localMids } = next;

    if (renegotiation) {
      const { reset } = renegotiation;

      if (reset) {
        this.watchdog.start();
      }

      if (this.isOfferer) {
        this.negotiateOffer({ reset });
      } else {
        // Answerer simply notifies the offerer
        await this.sendNegotiationNeeded({ reset, mids: localMids.toJSON() });
      }

      return next.doneRenegotiation();
    } else {
      return next;
    }
  };

  private scheduleConnectionTimeoutCheck = async (prev: NegotiationState, current: NegotiationState) => {
    const { connectionState: prevState } = prev;
    const { connectionState: currentState } = current;

    if (prevState !== currentState) {
      switch (currentState) {
        case RTC_ON:
          this.watchdog.stop();
          this.emit(PeerConnectionEvents.connectionTimeout, undefined);
          break;
        case RTC_CONNECTING:
          this.watchdog.start();
          break;
        default:
          break;
      }
    }

    return current;
  };

  private checkState = async (prev: NegotiationState, current: NegotiationState) => {
    if (prev.error !== current.error) {
      this.emit(PeerConnectionEvents.connectionError, current.error);
    }

    if (prev.connectionState !== current.connectionState) {
      this.emit(PeerConnectionEvents.connectionStateChange, current.connectionState);
    }

    return current;
  };

  private checkRemoteStreams = async (prev: NegotiationState, current: NegotiationState) => {
    if (prev.remoteStreams.audio !== current.remoteStreams.audio) {
      this.emit(PeerConnectionEvents.remoteAudioStream, current.remoteStreams.audio);
    }

    if (prev.remoteStreams.video !== current.remoteStreams.video) {
      this.emit(PeerConnectionEvents.remoteVideoStream, current.remoteStreams.video);
    }

    if (prev.remoteStreams.screen !== current.remoteStreams.screen) {
      this.emit(PeerConnectionEvents.remoteScreenStream, current.remoteStreams.screen);
    }

    return current;
  };

  private checkLocalStreams = async (prev: NegotiationState, current: NegotiationState) => {
    const { connection, transceivers, channels, localStreams } = current;

    if (!connection) {
      return current;
    }

    return current.set('transceivers', await WebRTCApi.setConnectionTracks(
      connection,
      transceivers,
      channels,
      localStreams,
    ));
  };

  private checkMids = (prev: NegotiationState, current: NegotiationState) => {
    if (!is(prev.localMids, current.localMids) || !is(prev.remoteMids, current.remoteMids)) {
      current.statsSender?.sendMids({
        localMids: current.localMids,
        remoteMids: current.remoteMids,
      });
    }
    return current;
  };

  /**
   * Answerer negotiation, receive offer, send answer
   * This is called by socket.io and the returned answer is sent back
   * to the offerer via ACK callback. If the function throws, then the
   * `send.offer()` will throw on the offerer side.
   */
  private negotiateAnswer = async ({
    offer,
    reset,
    mids,
  }: ISdpOfferMessage): Promise<ISdpAnswerMessage> => {
    this.state.update((state) => (
      !state.connection ? state.start() :
      reset ? state.reboot() :
      state
    ));

    this.state.update((state) =>
      mids
      ? state.set('remoteMids', new Mids(mids))
      : state,
    );

    const result = this.state.update(async (state, cancelToken) => {
      const { connection } = state;
      if (!connection) {
        return state;
      }

      const description = new RTCSessionDescription({ type: 'offer', sdp: offer });
      await connection.setRemoteDescription(description);

      throwIfCancelled(cancelToken);

      const answerObject = await WebRTCApi.createAnswer(connection);

      throwIfCancelled(cancelToken);

      await connection.setLocalDescription(answerObject);

      return state.merge({ offer, answer: answerObject.sdp });
    });

    const { answer, localMids } = await result;
    return {
      answer,
      mids: localMids.toJSON(),
    };
  };

  private receiveIceCandidate = async ({ candidate }: IIceCandidateMessage) => {
    await this.state.update(state => state.addIceCandidate(candidate));
    return null;
  };

  private receiveNegotiationNeeded = ({ reset = false, mids }: INegotiationNeededMessage) => {
    this.state.update((state) => state.set('remoteMids', new Mids(mids)));

    this.debounced.onNegotiationNeeded({
      reset,
      reason: RenegotiationReason.RemoteEvent,
    });
    return null;
  };

  private onNegotiationNeeded = ({
    reset = false,
    reason = RenegotiationReason.LocalEvent,
  }: INegotiationNeededMessage & IRenegotiationInit) => {
    this.state.update((state) => state.setRenegotiationIntent({ reset, reason }));
  };

  // Sent by the answerer and triggers receiveNegotiationNeeded on the offerer's side
  private sendNegotiationNeeded = ({ reset, mids }: Pick<INegotiationNeededMessage, 'reset' | 'mids'>) => {
    return this.send.negotiationNeeded({ reset, mids });
  };

  // Offerer negotiation: create offer, send it and receive as ACK on
  // the socket
  private negotiateOffer = ({ reset }: Pick<INegotiationNeededMessage, 'reset'>) => {
    this.state.update((state) => (
      !state.connection ? state.start() :
      reset ? state.reboot() :
      state
    ));

    this.state.update(async (state, cancelToken) => {
      try {
        const { connection, channels, remoteMids } = state;

        if (!connection) {
          return state;
        }
        const offerObject = await WebRTCApi.createOffer(connection, channels, remoteMids);

        throwIfCancelled(cancelToken);

        await connection.setLocalDescription(offerObject);

        return state.merge({ offer: offerObject.sdp });
      } catch (error) {
        if (!cancelToken.isCancelled()) {
          this.debounced.onNegotiationNeeded({
            reset: true,
            reason: RenegotiationReason.LocalOfferFail,
          });
        }
        throw error;
      }
    });

    this.state.update(async (state, cancelToken) => {
      try {
        const { offer, connection } = state;

        if (!connection) {
          return state;
        }
        // Send offer to the answerer, their response will be send back
        // in form of ACK message, returned as a promise from .send
        const { answer, mids: remoteMids } = (await this.send.offer({
          offer,
          reset,
          mids: state.localMids.toJSON(),
        }));

        throwIfCancelled(cancelToken);

        const answerObject = new RTCSessionDescription({ type: 'answer', sdp: answer });

        await connection.setRemoteDescription(answerObject);

        return state.merge({ answer: answerObject.sdp, remoteMids: new Mids(remoteMids) });
      } catch (error) {
        if (!cancelToken.isCancelled()) {
          this.debounced.onNegotiationNeeded({
            reset: true,
            reason: RenegotiationReason.RemoteAnswerFail,
          });
        }

        throw error;
      }
    });
  };

  private onRemoteTrack = ({ track, transceiver, streams }: Optional<RTCTrackEvent, 'transceiver'>) => {
    // Transceiver is not passed in Safari 12.0 and under
    const mid = transceiver?.mid;

    if (mid) {
      this.state.update((state) => state.setRemoteTrack(mid, track));
    } else {
      streams.forEach((stream) => {
        this.onRemoteStream({ stream });
      });
    }
  };

  private onRemoteStream = ({ stream }: Pick<MediaStreamEvent, 'stream'>) => {
    if (!stream) {
      return;
    }

    this.state.update((state) => state.setRemoteStreamFallback(stream));

    // Edge is modifying an existing stream instead of firing a new
    // `addstream` each time a channel is added.
    stream.addEventListener('addtrack', () => this.state.update((state) => state.setRemoteStreamFallback(stream)));
  };

  private onConnectionStateChange = () => {
    this.state.update(state => state); // trigger middleware
  };

  private onIceCandidate = ({ candidate }: RTCPeerConnectionIceEvent) => {
    this.state.update((state, cancelToken) => {
      // Don't send ice candidates if SDP exchange failed, a new
      // connection will be created (reset flag for renegotiation) so
      // these candidates can be discarded
      if (state.readyForICE()) {
        // Don't `await` here. This will cause process queues of offerer
        // and answerer to mutually deadlock.
        this.send.iceCandidate({ candidate }).catch((error) => {
          // Because this is executed out-of-queue, it can't piggy
          // back on the middleware checking for cancellation and
          // needs to do it explicitly here
          if (!cancelToken.isCancelled()) {
            notify(error, { name: 'PeerConnection' });
          }
        });
      }

      return state;
    });
  };
}
